/**
 * Real-data access for the Tandem console — no sample data anywhere.
 *
 * Reads the existing Tools endpoints (/visitsummary, /myvisits,
 * /visitautomationwindow, /weeklyvisitsummary) plus the console's own
 * /tandem/* ones. Engine concepts with no backend yet are Phase 2 and the UI
 * says so rather than standing anything in.
 */

import { useMemo } from "react";
import axios from "../../plugins/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildOccasions } from "./points";
import type { PointsConfig } from "./points";

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

// ── Date helpers (browser) ───────────────────────────────────────────────────
export const addDays = (d: Date, n: number) => {
    const c = new Date(d);
    c.setDate(c.getDate() + n);
    return c;
};
export const toISODate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// Monday of the week containing d (ISO week, Mon=0).
export const mondayOf = (d: Date) => addDays(d, -(((d.getDay() + 6) % 7)));
export const weekdayDates = (monday: Date) => [0, 1, 2, 3, 4].map((i) => addDays(monday, i));
export const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
/** Before today. Today itself is still bookable. */
export const isPastDay = (d: Date) => toISODate(d) < toISODate(new Date());
export const weekRangeLabel = (monday: Date) => {
    const fri = addDays(monday, 4);
    const m = monday.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit" });
    const f = fri.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
    return `${m} – ${f}`;
};
export const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
// All weekdays (Mon–Fri) of the month containing d.
export const monthWeekdays = (d: Date) => {
    const out: Date[] = [];
    const y = d.getFullYear();
    const m = d.getMonth();
    for (let day = 1; day <= 31; day++) {
        const dt = new Date(y, m, day);
        if (dt.getMonth() !== m) break;
        const dow = dt.getDay();
        if (dow !== 0 && dow !== 6) out.push(dt);
    }
    return out;
};

// ── /visitautomationwindow (unauthenticated; fixed 28-day window) ─────────────
export interface VisitAutomationWindow {
    from: string;
    to: string;
}
const fetchVisitAutomationWindow = async (): Promise<VisitAutomationWindow> => {
    const { data } = await axios.get<{ item1: string; item2: string }>(`${apiUrl}/visitautomationwindow`);
    return { from: data.item1, to: data.item2 };
};
export const useVisitAutomationWindow = () =>
    useQuery({
        queryKey: ["tandem", "visit-automation-window"],
        queryFn: fetchVisitAutomationWindow,
        staleTime: 1000 * 60 * 60,
    });

// ── /weeklyvisitsummary (HSAdmin) — the consultants + schedule source ─────────
export interface VisitDayT {
    date: string; // "d/M" for a real day; a label ("U","C","BD","TAD","TLD","Wk …") for summary rows
    bookingCount: number;
    dayStatus: string;
    consultantEmail?: string;
    dateR: string; // ISO datetime; only real weekday cells carry a real date
    actSiteMins?: number | null;
    writeUpMins?: number | null;
    travelTimeMins?: number;
    comment?: string;
    dayCount?: number | null; // numeric value on summary rows
}
export interface VisitConsultantT {
    email: string;
    name: string;
    fte?: number | null;
    jobTitle?: string;
    data: VisitDayT[];
}
export interface VisitRegionT {
    regionName: string;
    consultants: VisitConsultantT[];
}
export interface WeeklyVisitSummary {
    regions: VisitRegionT[];
    bookingCount: number;
    utilisation?: number | null;
    totalAvailableDays?: number | null;
    totalLostDays?: number | null;
    highestBookingPerson?: string;
    highestBookingCount?: number;
    lowestBookingPerson?: string;
    lowestBookingCount?: number;
    averageBookings?: number | null;
    averageBookingsPrevPeriod?: number | null;
}

const fetchWeeklyVisitSummary = async (dateISO: string): Promise<WeeklyVisitSummary | null> => {
    // "AllRegions" is the endpoint's own parameter, not a Tandem filter; the
    // console filters by team and week client-side.
    const { data } = await axios.get<WeeklyVisitSummary | null>(
        `${apiUrl}/weeklyvisitsummary/${dateISO}/AllRegions/AllTime`,
    );
    return data ?? null;
};
export const useWeeklyVisitSummary = (dateISO: string, enabled: boolean) =>
    useQuery({
        queryKey: ["tandem", "weekly-visit-summary", dateISO],
        queryFn: () => fetchWeeklyVisitSummary(dateISO),
        enabled,
        staleTime: 1000 * 60 * 5,
    });

// Pull the numeric value of a summary row (Date label = "U", "C", "BD", …).
export const summaryValue = (c: VisitConsultantT, label: string): number | null => {
    const row = c.data?.find((d) => d.date === label);
    return row?.dayCount ?? null;
};
// Real weekday cells carry a parseable dateR (year > 1900); summary/weekend rows don't.
export const dayCellsFor = (c: VisitConsultantT, day: Date): VisitDayT | undefined =>
    c.data?.find((d) => {
        if (!d.dateR) return false;
        const dr = new Date(d.dateR);
        return dr.getFullYear() > 1900 && sameDay(dr, day);
    });

/** A diary exception on a day: the free-text comment plus its TYPE letter (H/T/A/S/P). */
export type DayException = { comment: string; status: string | null } | null;

/** The exception the weekly summary records for a consultant on a day, if any. */
export const dayExceptionFor = (c: VisitConsultantT, day: Date): DayException => {
    const cell = dayCellsFor(c, day);
    return cell?.comment ? { comment: cell.comment, status: cell.dayStatus ?? null } : null;
};

/**
 * Days used against working days, over exactly these dates. A visit, an admin day
 * or a training day USES the day; holiday and sickness take it out of the working
 * days. Decided on the exception TYPE, never the comment text. The one
 * implementation every screen uses, so the days measure cannot drift between them.
 */
export const daysUsage = (dates: Date[], visits: SfVisit[], exceptionFor: (d: Date) => DayException) => {
    let used = 0;
    let available = 0;
    dates.forEach((d) => {
        const ex = exceptionFor(d);
        const kind = ex ? (ex.status || (ex.comment === "Admin" ? "A" : "?")).toUpperCase() : "";
        if (kind === "H" || kind === "S" || kind === "?") return;
        available += 1;
        const hasVisit = visits.some((v) => v.visitStartDate && sameDay(new Date(v.visitStartDate as string), d));
        if (hasVisit || kind === "A" || kind === "T") used += 1;
    });
    return { used, available, pct: available > 0 ? Math.round((used / available) * 100) : null };
};

export const daysUtilisation = (dates: Date[], visits: SfVisit[], exceptionFor: (d: Date) => DayException) =>
    daysUsage(dates, visits, exceptionFor).pct;

// A single-visit day whose booked window is short enough that a second visit
// might fit. A CAPACITY hint only — a candidate, never a missed booking.
export const ROOM_THRESHOLD_MINS = 300;

// Returns null when the booked window is unknown. Never assume a duration —
// guessing turns a missing Salesforce end time into a KPI against a consultant.
export const bookedMins = (v: { visitStartDate?: string; visitEndDate?: string }): number | null => {
    const s = v.visitStartDate ? new Date(v.visitStartDate).getTime() : NaN;
    const e = v.visitEndDate ? new Date(v.visitEndDate).getTime() : NaN;
    const m = (e - s) / 60000;
    return isFinite(m) && m > 0 && m < 24 * 60 ? m : null;
};
export const roomForSecond = (dayVisits: Array<{ visitStartDate?: string; visitEndDate?: string }>) => {
    if (dayVisits.length !== 1) return false;
    const mins = bookedMins(dayVisits[0]);
    return mins != null && mins <= ROOM_THRESHOLD_MINS;
};

// The full roster of current H&S consultants. The weekly summary only knows
// consultants who HAVE visits, so without this an empty diary hides the person.
export interface TandemConsultant {
    id: string;
    name: string;
    email: string;
    region?: string | null;
    team?: string | null;
    position?: string | null;
    isHSAdmin: boolean;
}

export const fetchTandemConsultants = async (): Promise<TandemConsultant[]> => {
    const { data } = await axios.get<TandemConsultant[]>(`${apiUrl}/tandem/consultants`);
    return Array.isArray(data) ? data : [];
};

export const useTandemConsultants = () =>
    useQuery({
        queryKey: ["tandem", "consultants"],
        queryFn: fetchTandemConsultants,
        // The roster changes when someone joins or leaves, not during a session.
        staleTime: 1000 * 60 * 30,
    });

/** The consultant roster every screen builds its rows from. */
export const useActiveConsultants = () => {
    const roster = useTandemConsultants();
    const active = useMemo(() => roster.data || [], [roster.data]);
    return { ...roster, active };
};

/**
 * Email → team name, from the roster. Team is the console's organising
 * dimension (region was dropped), and it lives on the employee record rather
 * than the weekly summary, so any screen labelling rows by team needs this.
 *
 * Falls back to "No team" rather than an empty string: 19 of 84 consultants
 * have no team set, and they should read as a real, visible group instead of a
 * blank that looks like a rendering fault.
 */
export const useTeamOf = () => {
    const roster = useTandemConsultants();
    return useMemo(() => {
        const byEmail = new Map(
            (roster.data || []).map((c) => [(c.email || "").toLowerCase(), c.team || "No team"]),
        );
        return (email?: string | null) => byEmail.get((email || "").toLowerCase()) || "No team";
    }, [roster.data]);
};

/**
 * A predicate for the team filter, keyed on consultant email.
 *
 * Team lives on the employee record, not on the weekly visit summary, so any
 * screen filtering by team needs the roster. Shared so the schedule, the report
 * and the consultants list cannot end up filtering three different ways.
 *
 * Falls open while the roster loads: showing everyone briefly is better than
 * showing an empty screen and looking broken.
 */
export const useTeamFilter = (team: string) => {
    const roster = useTandemConsultants();
    return useMemo(() => {
        if (team === "AllTeams" || !roster.data?.length) return () => true;
        const byEmail = new Map(
            roster.data.map((c) => [(c.email || "").toLowerCase(), c.team || "Unknown"]),
        );
        return (email?: string | null) => byEmail.get((email || "").toLowerCase()) === team;
    }, [team, roster.data]);
};

// ── /tandem/consultantvisits (any authed user) ───────────────────────────────────
export interface SfVisit {
    id: string;
    name?: string; // visit number (SF record name)
    accountId?: string | null; // stable occasion grouping key
    allocatedToId?: string | null;
    visitingContactId?: string | null;
    visitingContactName?: string | null;
    accountName?: string;
    industry?: string | null; // Account.Industry — the points rate lookup
    fraRiskLevel?: string | null; // "High Risk" | "Low Risk" — the fire escalation
    highRiskFra?: boolean | null; // the older checkbox, read alongside the above
    latitude?: number | null; // Account.BillingLatitude — Salesforce's own geocode
    longitude?: number | null;
    geocodeAccuracy?: string | null; // Account.BillingGeocodeAccuracy — "City" is too coarse to pin
    siteCount?: number | null; // Account.No_of_Sites__c
    locationGroup?: string | null; // Account.Postcode_Brick__c (a postcode AREA)
    keyAccount?: boolean | null;
    description?: string;
    subActivity?: string; // visit type — the points classification input
    visitLength?: string | null; // e.g. "Half day"
    dueDate?: string | null;
    status?: string;
    visitCategory?: string; // "On Site" | "Remote" | null
    visitStartDate?: string; // booked window
    visitEndDate?: string;
    actualStartTime?: string; // actuals, when logged
    actualEndTime?: string;
    travelStartTime?: string; // travel window wraps the visit: depart → arrive back
    travelEndTime?: string;
    attendanceMins?: number | null; // computed server-side: actual time on site
    actTimeMins?: number | null;
    writeUpTimeMinutes?: number | null;
    // Account_H_S_Consultant__c — the consultant who OWNS the account. Not
    // necessarily the person who did the visit; see allocatedToName.
    accountConsultantName?: string;
    allocatedToName?: string;
    allocatedToEmail?: string;
    /** Visiting_Consultant_Email__c — the hand-entered one the old query also matched. */
    consultantEmail?: string;
    allocatedToRegion?: string | null;
    priorityLevel?: string | number | null;
    auditNumber?: string | null;
    bookedOn?: string | null;
    lastVisitDate?: string | null;
    notes?: string | null;
    additionalComments?: string | null;
}

// Deep link to the Salesforce Visit record. Host is org-specific (prod) —
// sandboxes use a different my.salesforce host.
export const sfVisitUrl = (id: string) =>
    `https://elliswhittam.lightning.force.com/lightning/r/Visit__c/${id}/view`;
export const fetchVisits = async (fromISO: string, toISO: string): Promise<SfVisit[]> => {
    const { data } = await axios.get<SfVisit[]>(`${apiUrl}/tandem/visits/${fromISO}/${toISO}`);
    return Array.isArray(data) ? data : [];
};
// Work owed but not booked, by DUE date. A separate query because an unbooked
// visit has no start date, so the schedule's start-date filter cannot see it.
export const fetchVisitPool = async (dueFromISO: string, dueToISO: string): Promise<SfVisit[]> => {
    const { data } = await axios.get<SfVisit[]>(`${apiUrl}/tandem/visitpool/${dueFromISO}/${dueToISO}`);
    return Array.isArray(data) ? data : [];
};

/** A contact at the account, for the Visiting contact picker. */
export interface AccountContact {
    id: string;
    /** Already formatted by the API as "Name (email) (phone)". */
    name: string;
    adviceTypes?: string[];
}

// Pre-dates Tandem and is not Tandem-scoped: /getcontacts is [Authorize] only.
export const fetchAccountContacts = async (accountId: string): Promise<AccountContact[]> => {
    const { data } = await axios.get<AccountContact[]>(
        `${apiUrl}/getcontacts/${encodeURIComponent(accountId)}`,
    );
    return Array.isArray(data) ? data : [];
};

/** Fetched when the Book dialog opens, not with the pool — one account at a time. */
export const useAccountContacts = (accountId?: string | null, enabled = true) =>
    useQuery({
        queryKey: ["tandem", "account-contacts", accountId],
        queryFn: () => fetchAccountContacts(accountId as string),
        enabled: !!accountId && enabled,
        staleTime: 1000 * 60 * 10,
    });

export interface BookVisitRequest {
    id: string;
    allocatedToId: string;
    /** Local UK date and times, sent apart so no zone can be inferred. */
    date: string;
    startTime: string;
    endTime: string;
    visitingContactId: string;
    visitCategory: string;
    visitLength?: string;
    notes?: string;
    /** Not sent — used to invalidate only this consultant's cached visits. */
    consultantEmail?: string | null;
}

export interface BookVisitResult {
    booked: boolean;
    message: string;
    visitStart?: string | null;
    visitEnd?: string | null;
}

/** A refusal comes back as `booked: false` with a message, not as an error. */
export const useBookVisit = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (body: BookVisitRequest): Promise<BookVisitResult> => {
            const { data } = await axios.post<BookVisitResult>(`${apiUrl}/tandem/bookvisit`, body);
            return data;
        },
        onSuccess: (result, body) => {
            if (!result?.booked) return;
            // The weekly summary is what every count, bar and heatmap elsewhere is drawn from.
            qc.invalidateQueries({ queryKey: ["tandem", "visit-pool"] });
            qc.invalidateQueries({ queryKey: ["tandem", "weekly-visit-summary"] });
            // One consultant only: the prefix would refetch the whole roster at once.
            qc.invalidateQueries({
                predicate: (q) =>
                    q.queryKey[0] === "tandem" &&
                    q.queryKey[1] === "consultant-visits" &&
                    (q.queryKey[4] as string | null)?.toLowerCase() === body.consultantEmail?.toLowerCase(),
            });
        },
    });
};

export const useVisitPool = (dueFromISO: string, dueToISO: string) =>
    useQuery({
        queryKey: ["tandem", "visit-pool", dueFromISO, dueToISO],
        queryFn: () => fetchVisitPool(dueFromISO, dueToISO),
        staleTime: 1000 * 60 * 5,
        // Widening the horizon is a new key, so a fresh multi-megabyte fetch. Without
        // this the whole screen, controls included, is replaced by a spinner.
        placeholderData: (prev: SfVisit[] | undefined) => prev,
    });

// ── Scoring one consultant's period ─────────────────────────────────────────
/**
 * Recognition and capacity points for ONE consultant's visits in a period.
 *
 * Shared by the report and the consultants list so the same period cannot be
 * scored two different ways by two screens. Pass a single consultant's visits —
 * `buildOccasions` groups by account and day only, so a mixed list would merge
 * two consultants who visited the same account on the same day.
 *
 * Remote visits sit outside occasions (they can never earn a report supplement)
 * and are added at the flat remote rate.
 *
 * An occasion only admits visits flagged "On Site", and a visit with NO flag is
 * not Remote either — so it used to fall into a gap: excluded from the occasion
 * AND not counted as remote, contributing nothing. A whole period of booked work
 * could total zero, which is what the report and the consultants list were
 * showing. The flag is only set once a visit is arranged, so it is missing on
 * plenty of upcoming work. Unset is therefore treated as on-site, exactly as the
 * schedule's day totals already do: the visit is in the diary, the work is real,
 * and only an admin field is absent. `modeAssumed` counts those so a screen can
 * say the total rests on it.
 */
export const scoreConsultantPeriod = (
    visits: SfVisit[],
    config: PointsConfig,
): { points: number; capacity: number; industryMissing: number; modeAssumed: number } => {
    const occasions = buildOccasions(
        visits.map((v) => ({
            id: v.id,
            accountId: v.accountId,
            accountName: v.accountName,
            date: v.visitStartDate ? toISODate(new Date(v.visitStartDate)) : "",
            subActivity: v.subActivity,
            name: v.name,
            visitCategory: v.visitCategory || "On Site",
            industry: v.industry,
            fraRiskLevel: v.fraRiskLevel,
            highRiskFra: v.highRiskFra,
        })),
        config,
    );
    const remoteScored = visits
        .filter((v) => v.visitCategory === "Remote")
        .reduce((n) => n + config.remotePoints, 0);

    return {
        points: Math.round((occasions.reduce((n, o) => n + (o.recognition ?? 0), 0) + remoteScored) * 10) / 10,
        // Capacity excludes the supplement, so it can't exceed the day's cost.
        capacity: Math.round((occasions.reduce((n, o) => n + (o.capacity ?? 0), 0) + remoteScored) * 10) / 10,
        // Visits that DID score, off an account with no industry — the chase
        // list for Salesforce, not a scoring gap.
        industryMissing: occasions.reduce((n, o) => n + o.industryMissingCount, 0),
        modeAssumed: visits.filter((v) => v.visitCategory == null || v.visitCategory === "").length,
    };
};

/** Every consultant's visits for the period, in ONE request. */
export const useVisits = (fromISO: string, toISO: string, enabled = true) =>
    useQuery({
        queryKey: ["tandem", "visits", fromISO, toISO],
        queryFn: () => fetchVisits(fromISO, toISO),
        enabled,
        staleTime: 1000 * 60 * 5,
    });

/** Grouped by consultant. The visit carries only the Employee Id; the roster
 *  already maps it to an email, so the join happens here. */
export const useVisitsByConsultant = (fromISO: string, toISO: string, enabled = true) => {
    const q = useVisits(fromISO, toISO, enabled);
    const roster = useActiveConsultants();
    const byEmail = useMemo(() => {
        const emailOf = new Map<string, string>();
        for (const c of roster.active) if (c.id && c.email) emailOf.set(c.id, c.email.toLowerCase());
        const m = new Map<string, SfVisit[]>();
        for (const v of q.data ?? []) {
            // Fall back to the hand-entered email for anyone off the roster.
            const k = (v.allocatedToId && emailOf.get(v.allocatedToId)) || (v.consultantEmail || "").toLowerCase();
            if (!k) continue;
            const list = m.get(k);
            if (list) list.push(v);
            else m.set(k, [v]);
        }
        return m;
    }, [q.data, roster.active]);
    return { ...q, byEmail };
};

/** One consultant, still one request — used where only that person is on screen. */
export const useConsultantVisits = (fromISO: string, toISO: string, email: string | null, enabled: boolean) => {
    const q = useVisitsByConsultant(fromISO, toISO, enabled && !!email);
    const key = (email || "").toLowerCase();
    const data = useMemo(() => (key ? q.byEmail.get(key) ?? [] : undefined), [q.byEmail, key]);
    return { ...q, data };
};

// ── /vars — server config, for Salesforce record links ───────────────────────

/**
 * Builds a Salesforce Lightning record link. The host comes from the API, which
 * derives it from whichever org it is pointed at, so a test build cannot link to
 * prod records. Returns null when the API supplied no host, and callers render
 * nothing rather than a broken link.
 */
export interface DiaryDay {
    email: string;
    date: string;
    status: "H" | "T" | "A" | "S" | "P";
}

/** Roster diary for any range, one row per person per weekday; the week/month summary can't span longer. */
export const useDiary = (fromISO: string, toISO: string, enabled = true) => {
    const q = useQuery({
        queryKey: ["tandem", "diary", fromISO, toISO],
        queryFn: async () => {
            const { data } = await axios.get<DiaryDay[]>(`${apiUrl}/tandem/diary/${fromISO}/${toISO}`);
            return Array.isArray(data) ? data : [];
        },
        enabled,
        staleTime: 1000 * 60 * 5,
    });
    const byEmail = useMemo(() => {
        const m = new Map<string, Map<string, DiaryDay["status"]>>();
        for (const d of q.data ?? []) {
            const k = d.email.toLowerCase();
            const days = m.get(k) ?? new Map<string, DiaryDay["status"]>();
            days.set(d.date, d.status);
            m.set(k, days);
        }
        return m;
    }, [q.data]);
    return { ...q, byEmail };
};

export const useSalesforceLink = () => {
    const { data } = useQuery({
        queryKey: ["tandem", "server-vars"],
        queryFn: async () => {
            const { data } = await axios.get<{ salesforceUrl?: string | null }>(`${apiUrl}/vars`);
            return data ?? {};
        },
        staleTime: Infinity,
        retry: false,
    });

    const base = data?.salesforceUrl;
    return (objectType: string, id?: string | null) =>
        base && id ? `${base}/lightning/r/${objectType}/${id}/view` : null;
};
